import React, { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

export const ExitIntentPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [triggerType, setTriggerType] = useState<"exit" | "return">("exit");
  const leaveTimeRef = useRef<number | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    // Only run on desktop
    if (window.innerWidth < 1024) return;

    // DEBUG: Session check commented out for testing
    // const sessionShown = sessionStorage.getItem("exit_intent_shown");
    // if (sessionShown) return;

    // 1. Exit Intent (Mouse Leave)
    const handleMouseLeave = (e: MouseEvent) => {
      // Relaxed threshold: < 50px from top to catch fast movements
      if (e.clientY < 50 && !hasTriggered) {
        console.log("Exit intent triggered!"); 
        setTriggerType("exit");
        setIsOpen(true);
        setHasTriggered(true);
        // sessionStorage.setItem("exit_intent_shown", "true");
      }
    };

    // 2. Welcome Back (Tab Switch) - Debug Verbose
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("Tab HIDDEN");
        leaveTimeRef.current = Date.now();
      } else {
        console.log("Tab VISIBLE");
        if (leaveTimeRef.current) {
          const timeGone = Date.now() - leaveTimeRef.current;
          console.log("Gone for (ms):", timeGone);
          
          if (timeGone > 2000 && !hasTriggered) {
            console.log("FIRING OPEN STATE NOW");
            setTriggerType("return");
            setIsOpen(true);
            setHasTriggered(true);
          }
        } else {
          console.log("No leave time recorded (ref is null)");
        }
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hasTriggered]);

  const handleBookNow = () => {
    window.location.href = import.meta.env.VITE_BOOKING_URL;
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md md:max-w-lg p-0 gap-0 overflow-hidden border-none shadow-2xl bg-white">
        <div className="relative flex flex-col md:flex-row h-full">
          {/* Image Section (Left) */}
          <div className="hidden md:block w-2/5 relative">
            <img 
              src="/images/spa-balcony.webp"
              alt="Relaxing Spa Balcony"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                // Fallback if specific image fails
                (e.target as HTMLImageElement).src = "/images/hero-spring.jpg";
              }}
            />
            <div className="absolute inset-0 bg-pine-green/20" />
            <div className="absolute bottom-6 left-6 text-white">
              <div className="font-playfair font-bold text-2xl leading-tight drop-shadow-md">
                Wait!
              </div>
              <div className="text-sm font-light opacity-95 drop-shadow-sm mt-1">
                Don't miss out.
              </div>
            </div>
          </div>

          {/* Content Section (Right) */}
          <div className="w-full md:w-3/5 p-8 flex flex-col justify-center bg-white relative">
            {/* Close Button Removed - Using Default DialogClose */}

            <DialogHeader className="mb-4">
              <DialogTitle className="font-playfair text-2xl text-neutral-900 mb-1 text-left">
                {triggerType === "return" ? "Welcome Back!" : "Wait! Before you go..."}
              </DialogTitle>
              <DialogDescription className="text-gray-600 text-left">
                {triggerType === "return" 
                  ? "Still comparing? Book direct right now to unlock these exclusive perks:" 
                  : "Don't miss out. Get exclusive perks when you book directly with us right now."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="bg-alpine-gold/10 p-2 rounded-full mt-0.5">
                  <Clock className="w-4 h-4 text-alpine-gold" />
                </div>
                <div>
                  <div className="font-semibold text-neutral-900 text-sm">Free Late Checkout (12:00)</div>
                  <div className="text-xs text-gray-500">Sleep in. Relax. No rush.</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full mt-0.5">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-neutral-900 text-sm">Best Rate Guaranteed</div>
                  <div className="text-xs text-gray-500">We match any lower price you find.</div>
                </div>
              </div>
            </div>

            <Button 
              onClick={handleBookNow}
              className="w-full bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg group"
              size="lg"
            >
              Claim My Benefits
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <div className="mt-3 text-center">
              <button 
                onClick={() => setIsOpen(false)}
                className="text-xs text-gray-400 hover:text-gray-600 underline decoration-gray-300 underline-offset-2"
              >
                No thanks, I'll pay full price
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
